// frontend/client/src/components/TermsOfService.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, UserCheck, Server, Shield, Copyright, CreditCard, AlertTriangle, Scale, Info, Mail } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  const Section = ({ icon: Icon, title, children }) => (
    <section className="mb-10">
      <div className="flex items-start mb-4">
        <Icon className="w-6 h-6 text-pink-600 mr-3 mt-1 flex-shrink-0" />
        <h2 className="text-2xl font-semibold text-gray-900">{title}</h2>
      </div>
      <div className="ml-9 space-y-4 text-gray-700">
        {children}
      </div>
    </section>
  );

  const SubSection = ({ title, children }) => (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-pink-600 hover:text-pink-500 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
        
        <div className="bg-white rounded-xl p-8 md:p-12 border border-gray-200 shadow-xl">
          <div className="flex items-center mb-6">
            <FileText className="w-10 h-10 text-pink-600 mr-4" />
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Terms of Service</h1>
              <p className="text-gray-500 mt-2">Last updated: June 10, 2026</p>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-8">
            <Section icon={UserCheck} title="1. Acceptance of Terms">
              <SubSection title="1.1 Agreement">
                <p className="text-gray-600">
                  By accessing or using Infleuince ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
                  If you do not agree to these Terms, you may not use the Service.
                </p>
              </SubSection>

              <SubSection title="1.2 Changes to Terms">
                <p className="text-gray-600 mb-2">
                  We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or in-app notification. 
                  Continued use of the Service after changes constitutes acceptance of the new Terms.
                </p>
              </SubSection>

              <SubSection title="1.3 Eligibility">
                <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                  <li>You must be at least 13 years old to use the Service.</li>
                  <li>Users between 13-17 require parental consent loops.</li>
                  <li>You must provide accurate registration information and maintain total account authentication security.</li>
                </ul>
              </SubSection>
            </Section>

            <Section icon={Server} title="2. Description of Service & Generative Frameworks">
              <SubSection title="2.1 Service Overview">
                <p className="mb-2 text-gray-700">Infleuince acts as an AI-powered content orchestration platform providing vertical video editing, content scheduling, and direct publishing optimizations.</p>
              </SubSection>

              <SubSection title="2.2 Multi-Engine Prompt Routing Notice">
                <p className="text-gray-600">
                  To synthesize text caption files, evaluate uploaded video frame sprites, and customize text structures, the system passes filtered context prompts dynamically through third-party infrastructure components (including Anthropic Claude, Google Gemini, OpenAI GPT, and DeepSeek AI engines).
                </p>
              </SubSection>

              <SubSection title="2.3 Automated Cross-Platform Cross-Promotion">
                <p className="text-gray-600">
                  When you activate our programmatic publishing channels (such as One-Click Presence systems), the software initiates automated workflows. This includes analyzing live broadcast links from primary channels (e.g., TikTok, Instagram) and using fallback AI models to auto-draft secondary announcements to connected networks (e.g., X/Twitter, LinkedIn).
                </p>
              </SubSection>
            </Section>

            <Section icon={UserCheck} title="3. User Accounts & Third-Party Platform Access">
              <SubSection title="3.1 Native Platform Integration Requirements">
                <p className="text-gray-600">
                  By linking platform profiles, you authorize Infleuince to leverage your granted permissions to perform publishing, reading, and structural analytics operations on your behalf. You acknowledge that our platform works within third-party restrictions and respect each native channel's Developer Guidelines.
                </p>
              </SubSection>
            </Section>

            <Section icon={Shield} title="4. Acceptable Use and Safety Restrictions">
              <SubSection title="4.1 Content Responsibilities">
                <p className="text-gray-600">
                  You are solely responsible for checking the factual validity, trademark permissions, and community compliance status of every content package before executing a live publication block.
                </p>
              </SubSection>
            </Section>

            <Section icon={CreditCard} title="7. Plan Resource Limits & Monthly Generative Credits">
              <SubSection title="7.1 Credit Pools and Operations">
                <p className="text-gray-600">
                  Tiered subscription tiers specify a distinct pool of Monthly Generative Credits. High-overhead background tasks—including automated visual frame rendering, speech conversions, multi-engine prompt generations, and bulk variant analyses—deduct varying amounts from your available credits balance.
                </p>
              </SubSection>

              <SubSection title="7.2 Non-Accumulation and Resets">
                <p className="text-gray-600">
                  Generative tokens follow a strict monthly expiration setup tied to your signup date. Unused points drop off at the end of each cycle and do not carry over to subsequent billing months.
                </p>
              </SubSection>
            </Section>

            <Section icon={AlertTriangle} title="8. Disclaimers and API Throttling Vulnerabilities">
              <SubSection title="8.1 System Throttling Limits Disclaimer">
                <p className="text-gray-600">
                  Infleuince implements systemic request rate delays to protect accounts from triggering platform safety blocks (such as maximum post limitations per minute on TikTok). We accept no operational responsibility for delayed post-distribution events, failed schedule queues, or connection status adjustments caused by external API throttling.
                </p>
              </SubSection>
            </Section>

            <Section icon={AlertTriangle} title="9. Limitation of Liability">
              <SubSection title="9.1 Maximum Liability Limits">
                <div className="bg-gray-100 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-700 text-sm uppercase font-semibold">
                    OUR TOTAL LIABILITY FOR ANY CLAIM LINKED TO APPLIED AUTOMATION INTERFACES SHALL NOT EXCEED THE TOTAL AGGREGATE FEES COLLECTED FOR YOUR ACCOUNT TIER OVER THE PRECEDING TWELVE MONTHS.
                  </p>
                </div>
              </SubSection>
            </Section>

            <Section icon={Mail} title="13. Contact Information">
              <SubSection title="13.1 Legal Reachability Details">
                <p className="mb-2 text-gray-700">Send inquiries related to updates to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-gray-600">
                  <li>Email: <a href="mailto:legal@infleuince.com" className="text-pink-600 hover:text-pink-500">legal@infleuince.com</a></li>
                  <li>Company: Infleuince, Delaware C-Corp</li>
                </ul>
              </SubSection>
            </Section>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                These Terms of Service are effective as of June 10, 2026 and apply to all users of the Infleuince service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}